/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';

import { css, StyleSheet } from 'aphrodite';
import { cloneDeep, isNil, isEmpty } from 'lodash';
import { ActionButton, Modal, Text } from '@blend/enterprise-components';
import { ModalContent } from 'blb-components/ModalContent';
import { useContext } from 'react';
import { FeesContext } from '../../containers/FeesTab';
import { CancelRequest, FeeFormValue } from '../../types';
import { Pricing } from 'blb-types/pricing';
import { getFeeIndexById } from '../../utils/closingFeesUtils';

const styles = StyleSheet.create({
  footerBar: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 28,
  },
});

export interface CancelRequestModalProps {
  cancelRequest: CancelRequest | null;
  handleClose: () => void;
  isOpen: boolean;
}

const UPDATE_TYPE_TO_COPY = {
  [Pricing.UpdateType.ADD]: 'add fee request',
  [Pricing.UpdateType.DELETE]: 'fee delete request',
  [Pricing.UpdateType.EDIT]: 'fee edit request',
};

export const CancelRequestModal: React.FC<CancelRequestModalProps> = ({ handleClose, cancelRequest, isOpen }) => {
  const { formValues, setFormValues, setFormInitialValues, formInitialValues } = useContext(FeesContext);

  const updateFormInitialValues = (cancelRequest: CancelRequest, newCurrentFee: FeeFormValue) => {
    const { feeId, subsectionKey } = cancelRequest;
    const initialSubsection = cloneDeep(formInitialValues[subsectionKey]);
    if (!isNil(initialSubsection) && !isEmpty(initialSubsection)) {
      const feeIndex = getFeeIndexById(initialSubsection, feeId);
      initialSubsection[feeIndex] = { ...newCurrentFee, isRevertedFee: true };
      setFormInitialValues({
        ...formInitialValues,
        ...{ [subsectionKey]: initialSubsection },
      });
    }
  };

  const confirmCancel = (cancelRequest: CancelRequest) => {
    const { feeId, subsectionKey } = cancelRequest;
    const subsection = formValues[subsectionKey];

    if (!isNil(subsection) && !isEmpty(subsection)) {
      let formSubsection = cloneDeep(subsection);
      const currentFeeIndex = getFeeIndexById(formSubsection, feeId);
      const previousFee = subsection[currentFeeIndex].previousApprovedFee;

      if (previousFee) {
        // We lose sharedInboxThreadId when reverting to previous fee
        // Make sure the threadId follows a fee
        const { sharedInboxThreadId, id } = formSubsection[currentFeeIndex];
        const newCurrentFee = { ...previousFee, id, sharedInboxThreadId };
        updateFormInitialValues(cancelRequest, newCurrentFee);
        formSubsection[currentFeeIndex] = newCurrentFee;
      } else {
        formSubsection = formSubsection.filter((_val, idx) => idx !== currentFeeIndex);
      }

      setFormValues({
        ...formValues,
        ...{ [subsectionKey]: formSubsection },
      });
    }

    handleClose();
  };

  return (
    <React.Fragment>
      {cancelRequest && (
        <Modal
          contentLabel={`Cancel ${UPDATE_TYPE_TO_COPY[cancelRequest.updateType]}`}
          handleClose={handleClose}
          isOpen={isOpen}
        >
          <ModalContent
            handleClose={handleClose}
            headerText={`Cancel ${UPDATE_TYPE_TO_COPY[cancelRequest.updateType]}`}
          >
            <Text fontSize="sm">
              {`You are cancelling the ${
                UPDATE_TYPE_TO_COPY[cancelRequest.updateType]
              }. The loan team will no longer recieve this request for review. Please
            confirm this action.`}
            </Text>

            <div className={css(styles.footerBar)}>
              <ActionButton id="NewRequestForm-cancel" label="Cancel" onClick={handleClose} theme="tertiary" />
              <ActionButton
                id="NewRequestForm-confirm"
                label="Confirm"
                onClick={() => confirmCancel(cancelRequest)}
                type="submit"
              />
            </div>
          </ModalContent>
        </Modal>
      )}
    </React.Fragment>
  );
};
